// Typed fetch wrapper. Always same-origin with credentials (session cookie).

export class ApiError extends Error {
  status: number;
  extra: Record<string, unknown>;
  constructor(status: number, message: string, extra: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.extra = extra;
  }
}

// 生の技術メッセージ（"Failed to fetch" や HTTP番号）を画面に出さず、
// 「何が起きて→次にどうするか」を平易な日本語で返す。status=0 は通信そのものの失敗。
function messageForStatus(status: number): string {
  if (status === 0) return "通信に失敗しました。電波・接続を確認して、もう一度お試しください。";
  if (status === 401) return "ログインの有効期限が切れました。お手数ですが、もう一度ログインしてください。";
  if (status === 403) return "この操作を行う権限がありません。管理者にご確認ください。";
  if (status === 404) return "対象が見つかりませんでした。すでに削除されたか、URLが変わった可能性があります。";
  if (status === 408 || status === 429) return "ただいま混み合っています。少し待ってから、もう一度お試しください。";
  if (status >= 500) return "ただいま処理できませんでした。少し待ってから、もう一度お試しください。";
  if (status >= 400) return "入力内容をご確認のうえ、もう一度お試しください。";
  return "エラーが発生しました。もう一度お試しください。";
}

// サーバの日本語メッセージ（data.error）を最優先し、無ければ status から平易な日本語に補完。
function apiError(status: number, data: { error?: unknown } = {}): ApiError {
  const serverMsg = typeof data.error === "string" && data.error.trim() ? data.error : null;
  return new ApiError(status, serverMsg ?? messageForStatus(status), data as Record<string, unknown>);
}

async function request<T>(method: string, path: string, body?: unknown, timeoutMs = 25000): Promise<T> {
  // ネットワーク不調で永遠に待ち続けないようタイムアウトを設ける（読み込みが止まらない問題対策）。
  // AIの読み取りなど時間のかかる処理だけ、呼び出し側で長めにする。
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: "same-origin",
      headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw new ApiError(0, "接続がタイムアウトしました。通信環境をご確認のうえ、再読み込みしてください。");
    // ネットワーク到達不可（オフライン等）。生の TypeError を出さず日本語で通知。
    throw apiError(0);
  } finally {
    clearTimeout(timer);
  }
  // モバイル回線などで本文読み取り中に接続が切れると res.text() が raw な "Load failed" を投げる。
  // try/catch で包み、日本語の案内に変換する（再読み込みで回復できる）。
  let text = "";
  try { text = await res.text(); }
  catch { throw new ApiError(0, "通信が途中で切れました。電波の良い場所で、更新（再読み込み）してください。"); }
  // res.ok の判定より前に JSON.parse で失敗しないよう、安全にパースする。
  let data: { error?: unknown } = {};
  if (text) { try { data = JSON.parse(text); } catch { data = {}; } }
  if (!res.ok) {
    throw apiError(res.status, data);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown, opts?: { timeoutMs?: number }) => request<T>("POST", path, body, opts?.timeoutMs),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
  upload: async <T>(path: string, form: FormData): Promise<T> => {
    let res: Response;
    try {
      res = await fetch(`/api${path}`, { method: "POST", credentials: "same-origin", body: form });
    } catch {
      throw apiError(0);
    }
    let data: { error?: unknown } = {};
    try { data = await res.json(); } catch { data = {}; }
    if (!res.ok) throw apiError(res.status, data);
    return data as T;
  },
  // open a file through a short-lived signed URL (never a public URL)
  openFile: async (versionId: string) => {
    const { url } = await request<{ url: string }>("GET", `/files/${versionId}/url`);
    window.open(url, "_blank");
  },
  // download a server-generated file (CSV 等) as an attachment
  download: async (path: string, fallbackName: string) => {
    let res: Response;
    try {
      res = await fetch(`/api${path}`, { method: "GET", credentials: "same-origin" });
    } catch {
      throw apiError(0);
    }
    if (!res.ok) {
      let data: { error?: unknown } = {};
      try { data = await res.json(); } catch { data = {}; }
      throw apiError(res.status, data);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
